import { useCallback, useEffect, useMemo, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import type { ActivityStatus } from "@torre/work-hours";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { backlogRoute } from "../config";
import ActivityCard from "../components/ActivityCard";
import ActividadFormModal from "../components/ActividadFormModal";
import ActividadDetailModal from "../components/ActividadDetailModal";
import { useCatalog, useSprints } from "../hooks/useActivityData";
import { useShowGithub } from "../hooks/useShowGithub";
import { activityErrorMessage, fetchActivities, fetchActivity, updateActivityStatus, type ActivitiesMeta, type ActivityRow } from "../services/activitiesService";
import { KANBAN_STATUSES, STATUS_BAR, STATUS_TONE, filterGithub, isOverdue, todayLocal, dayDiff } from "../lib/format";
import { isActivityStuck } from "@torre/work-hours";
import KpiStrip, { type KpiItem } from "../../../components/staff-ops/KpiStrip";
import { applyScope } from "../lib/activityScope";
import ActivityScopeFilters, { useActivityScope } from "../components/ActivityScopeFilters";

// ---------------------------------------------------------------------------
// Tablero Kanban del sprint ACTIVO (#1304). Solo lo que está en el sprint se
// trabaja y se muestra aquí; el backlog vive en su propia sección. Arrastrar
// una tarjeta a otra columna cambia su estado en el servidor.
// #1404: SIN sprint en curso el tablero no se queda vacío: muestra los tickets
// sin sprint (lo que nace de las horas registradas cae aquí) con el mismo
// interruptor «Issues de GitHub» del backlog.
// ---------------------------------------------------------------------------

const DRAG_MIME = "application/x-activity-id";

export default function ActividadesListSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const { catalog } = useCatalog();
  const { sprints, activeSprintId, loading: sprintsLoading } = useSprints();
  const [params, setParams] = useSearchParams();

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [meta, setMeta] = useState<ActivitiesMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [epics, setEpics] = useState<ActivityRow[]>([]);
  const [person, setPerson] = useState("");
  const [type, setType] = useState("");
  const [text, setText] = useState("");
  const [over, setOver] = useState<ActivityStatus | null>(null);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<ActivityRow | null>(null);
  const [showGithub, setShowGithub] = useShowGithub();
  // Alcance por cliente / proyecto / repositorio (en la URL, compartido con Backlog y Roadmap).
  const { scope, setScope, projects } = useActivityScope();

  const sprint = useMemo(() => sprints.find((s) => s.id === activeSprintId) ?? null, [sprints, activeSprintId]);

  const load = useCallback(async () => {
    if (sprintsLoading) return;
    setLoading(true);
    setError(null);
    try {
      // Sin sprint activo: todo lo que no tiene sprint (lo hecho, solo lo reciente).
      const res = activeSprintId ? await fetchActivities({ sprintId: activeSprintId, includeDone: true, limit: 2000 }) : await fetchActivities({ limit: 2000 });
      setRows(activeSprintId ? res.data : res.data.filter((a) => !a.sprintId));
      setMeta(res.meta);
    } catch (err) {
      setError(activityErrorMessage(err) ?? t("staffActivities.common.error"));
    } finally {
      setLoading(false);
    }
  }, [activeSprintId, sprintsLoading, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Épicas para el selector de padre y el chip de la tarjeta (todas, no solo las del sprint).
  useEffect(() => {
    fetchActivities({ includeDone: true, limit: 2000 })
      .then((res) => setEpics(res.data.filter((a) => a.isEpic || res.data.some((b) => b.parentId === a.id))))
      .catch(() => setEpics([]));
  }, []);

  // Deep link `?ticket=<id>` (lo usa Horas Trabajadas): abre el detalle.
  const ticketParam = params.get("ticket");
  useEffect(() => {
    if (!ticketParam || detail) return;
    const inRows = rows.find((a) => a.id === ticketParam);
    if (inRows) setDetail(inRows);
    else if (!loading) fetchActivity(ticketParam).then((a) => a && setDetail(a)).catch(() => undefined);
  }, [ticketParam, rows, loading, detail]);

  const closeDetail = () => {
    setDetail(null);
    if (ticketParam) {
      params.delete("ticket");
      setParams(params, { replace: true });
    }
  };

  const people = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of rows) if (a.assigneeTeamMemberId && a.assigneeName) map.set(a.assigneeTeamMemberId, a.assigneeName);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const typesInUse = useMemo(() => [...new Set(rows.map((a) => a.typeKey))], [rows]);
  const epicTitle = useCallback((id: string | null) => (id ? epics.find((e) => e.id === id)?.title ?? null : null), [epics]);

  const noSprint = !sprintsLoading && !activeSprintId;
  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    // Sin sprint el tablero sale del backlog: los issues de GitHub siguen el interruptor.
    const base = applyScope(noSprint ? filterGithub(rows, epics.length ? epics.concat(rows) : rows, showGithub) : rows, scope, projects);
    return base.filter(
      (a) =>
        !a.archived &&
        (!person || (person === "__none__" ? !a.assigneeTeamMemberId : a.assigneeTeamMemberId === person)) &&
        (!type || a.typeKey === type) &&
        (!q || a.title.toLowerCase().includes(q) || a.key.toLowerCase().includes(q)),
    );
  }, [rows, epics, noSprint, showGithub, person, type, text, scope, projects]);

  const byStatus = useMemo(() => {
    const out: Record<ActivityStatus, ActivityRow[]> = { backlog: [], todo: [], in_progress: [], review: [], blocked: [], done: [] };
    for (const a of filtered) (out[a.status] ?? out.todo).push(a);
    // Lo que quedó en `backlog` dentro del sprint se trabaja como «por hacer».
    out.todo = [...out.backlog, ...out.todo];
    out.backlog = [];
    return out;
  }, [filtered]);

  const replaceRow = (next: ActivityRow | null, id: string) => {
    setRows((prev) => (next ? prev.map((a) => (a.id === id ? { ...a, ...next } : a)) : prev.filter((a) => a.id !== id)));
  };

  const moveTo = async (id: string, status: ActivityStatus) => {
    const current = rows.find((a) => a.id === id);
    if (!current || current.status === status) return;
    const before = current;
    replaceRow({ ...current, status, statusChangedAt: new Date().toISOString() }, id);
    try {
      const saved = await updateActivityStatus(id, status);
      if (saved) replaceRow(saved, id);
    } catch (err) {
      replaceRow(before, id);
      setError(activityErrorMessage(err) ?? t("staffActivities.kanban.statusError"));
    }
  };

  const onDragStart = (e: DragEvent<HTMLDivElement>, a: ActivityRow) => {
    e.dataTransfer.setData(DRAG_MIME, a.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (e: DragEvent<HTMLDivElement>, status: ActivityStatus) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData(DRAG_MIME);
    if (id) void moveTo(id, status);
  };

  // #1595: indicadores del sprint encima del tablero.
  const kpis = useMemo((): KpiItem[] => {
    const now = new Date().toISOString();
    const pts = (list: ActivityRow[]) => list.reduce((n, a) => n + (Number(a.storyPoints) || 0), 0);
    const total = pts(filtered);
    const done = pts(filtered.filter((a) => a.status === "done"));
    const remaining = sprint ? dayDiff(todayLocal(), sprint.endDate) : null;
    return [
      { id: "cards", label: t("staffOps.kanban.cards"), value: filtered.length, icon: "ri-stack-line", tone: "primary", hint: sprint ? sprint.name : t("staffOps.kanban.noSprint") },
      { id: "points", label: t("staffOps.kanban.points"), value: `${done}/${total}`, icon: "ri-hashtag", tone: "success", progress: total ? done / total : null },
      { id: "inProgress", label: t("staffActivities.status.in_progress"), value: byStatus.in_progress.length, icon: "ri-loader-2-line", tone: "violet" },
      { id: "review", label: t("staffActivities.status.review"), value: byStatus.review.length, icon: "ri-eye-line", tone: "warning" },
      { id: "risk", label: t("staffOps.kanban.atRisk"), value: filtered.filter((a) => a.status === "blocked" || isActivityStuck(a, now) || isOverdue(a)).length, icon: "ri-alarm-warning-line", tone: "error", hint: t("staffOps.kanban.atRiskHint") },
      { id: "days", label: t("staffOps.kanban.daysLeft"), value: remaining == null ? "—" : Math.max(0, remaining), icon: "ri-calendar-event-line", tone: "info", hint: sprint ? `${sprint.startDate} → ${sprint.endDate}` : undefined },
    ];
  }, [filtered, byStatus, sprint, t]);

  const isOwner = Boolean(meta?.isOwner);
  const canManage = Boolean(meta?.canManage);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-6">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-500">
            <i className="ri-kanban-view text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>
              {t("staffActivities.kanban.title")}
              {sprint && <span className={`ml-2 text-base font-normal ${tc.textSecondary}`}>· {sprint.name}</span>}
            </h1>
            <p className={`text-sm ${tc.textSecondary}`}>
              {sprint ? `${sprint.startDate} → ${sprint.endDate}${sprint.goal ? ` · ${sprint.goal}` : ""}` : t("staffActivities.kanban.subtitle")}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={person} onChange={(e) => setPerson(e.target.value)} className={`rounded-lg border px-2 py-1.5 text-xs ${tc.border} ${tc.cardBg} ${tc.text}`} aria-label={t("staffActivities.kanban.filterPerson")}>
            <option value="">{t("staffActivities.kanban.filterPerson")}: {t("staffActivities.kanban.all")}</option>
            <option value="__none__">{t("staffActivities.kanban.unassigned")}</option>
            {people.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className={`rounded-lg border px-2 py-1.5 text-xs ${tc.border} ${tc.cardBg} ${tc.text}`} aria-label={t("staffActivities.kanban.filterType")}>
            <option value="">{t("staffActivities.kanban.filterType")}: {t("staffActivities.kanban.all")}</option>
            {typesInUse.map((k) => (
              <option key={k} value={k}>
                {catalog.find((ty) => ty.key === k)?.name ?? k}
              </option>
            ))}
          </select>
          <ActivityScopeFilters scope={scope} setScope={setScope} projects={projects} rows={rows} tc={tc} size="sm" />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("staffActivities.kanban.filterText")} className={`w-40 rounded-lg border px-2 py-1.5 text-xs ${tc.border} ${tc.cardBg} ${tc.text}`} aria-label={t("staffActivities.kanban.filterText")} />
          {noSprint && (
            <label className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs ${tc.border} ${tc.text}`} title={t("staffActivities.backlog.showGithubHint")}>
              <input type="checkbox" role="switch" aria-checked={showGithub} checked={showGithub} onChange={(e) => setShowGithub(e.target.checked)} className="h-3.5 w-3.5 accent-violet-500" data-testid="kanban-show-github" />
              <i className="ri-github-line" /> {t("staffActivities.backlog.showGithub")}
            </label>
          )}
          <button type="button" onClick={() => setCreating(true)} data-testid="kanban-new" className="flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-500">
            <i className="ri-add-line" /> {t("staffActivities.kanban.newActivity")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-3 rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-500" data-testid="kanban-error">
          {error}
        </div>
      )}

      <div className="px-6 pt-4">
        <KpiStrip items={kpis} loading={loading} />
      </div>

      {noSprint && (
        <div className={`mx-6 mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`} data-testid="kanban-no-sprint">
          <span>
            <i className="ri-calendar-close-line mr-1" />
            {t("staffActivities.kanban.noSprint")}
          </span>
          <Link to={backlogRoute} className="rounded-lg bg-violet-600 px-3 py-1 font-medium text-white hover:bg-violet-500">
            {t("staffActivities.kanban.goBacklog")}
          </Link>
        </div>
      )}
      <div className="flex flex-1 gap-3 overflow-x-auto p-6">
          {KANBAN_STATUSES.map((status) => {
            const list = byStatus[status];
            return (
              <div
                key={status}
                data-testid={`kanban-column-${status}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (over !== status) setOver(status);
                }}
                onDragLeave={() => over === status && setOver(null)}
                onDrop={(e) => onDrop(e, status)}
                className={`flex min-w-[260px] flex-1 flex-col overflow-hidden rounded-2xl border ${tc.cardBorder} ${isDarkMode ? "bg-dark-surface/60" : "bg-gray-100/80"} ${over === status ? "ring-2 ring-violet-500" : ""}`}
              >
                <div className={`h-1 w-full ${STATUS_BAR[status]}`} />
                <div className="flex items-center justify-between px-3 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[status]}`}>{t(`staffActivities.status.${status}`)}</span>
                  <span className={`flex items-center gap-2 text-[11px] ${tc.textSecondary}`}>
                    <span>{t("staffActivities.kanban.cards", { count: list.length })}</span>
                    <span className="rounded bg-subtle px-1.5 py-0.5 font-mono" data-testid={`kanban-points-${status}`} title={t("staffOps.kanban.points")}>
                      {list.reduce((n, a) => n + (Number(a.storyPoints) || 0), 0)} {t("staffActivities.card.points")}
                    </span>
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2">
                  {loading && list.length === 0 && <p className={`px-1 text-xs ${tc.textSecondary}`}>{t("staffActivities.common.loading")}</p>}
                  {!loading && list.length === 0 && <p className={`px-1 py-6 text-center text-xs ${tc.textSecondary}`}>{over === status ? t("staffActivities.kanban.dropHere") : t("staffActivities.kanban.empty")}</p>}
                  {list.map((a) => (
                    <ActivityCard key={a.id} activity={a} catalog={catalog} isOwner={isOwner} parentTitle={epicTitle(a.parentId)} tc={tc} onOpen={setDetail} draggable onDragStart={onDragStart} />
                  ))}
                </div>
              </div>
            );
          })}
      </div>

      {creating && (
        <ActividadFormModal
          mode="create"
          catalog={catalog}
          sprints={sprints}
          epics={epics}
          defaultSprintId={activeSprintId}
          onClose={() => setCreating(false)}
          onSaved={(a) => {
            setCreating(false);
            if ((a.sprintId ?? null) === (activeSprintId ?? null)) setRows((prev) => [a, ...prev.filter((x) => x.id !== a.id)]);
          }}
        />
      )}
      {detail && (
        <ActividadDetailModal
          activity={detail}
          catalog={catalog}
          sprints={sprints}
          epics={epics}
          isOwner={isOwner}
          canManage={canManage}
          onClose={closeDetail}
          onChanged={(next) => {
            replaceRow(next, detail.id);
            if (next) setDetail(next);
          }}
        />
      )}
    </div>
  );
}
